import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from './AuthContext';
import { useModalQueue } from './ModalQueueContext';
import { useDailyBonusStatus } from '@/hooks/useDailyBonus';
import { useVerificationStatusQuery } from '@/hooks/queries/useVerificationStatusQuery';
import { dailyBonusEvents } from '@/lib/daily-bonus-events';

interface StreakBrokenInfo {
  streakBroken: boolean;
  previousStreak: number | null;
}

interface DailyBonusContextType {
  showDailyBonusModal: boolean;
  setShowDailyBonusModal: (show: boolean) => void;
  openDailyBonusModal: () => void;
  dismissDailyBonusModal: () => void;
  // Expose status data for header badge
  canClaim: boolean;
  pendingCount: number;
  enabled: boolean;
  loading: boolean;
  refetch: () => void;
  // Streak broken notification state
  streakBrokenInfo: StreakBrokenInfo | null;
  setStreakBrokenInfo: (info: StreakBrokenInfo | null) => void;
  clearStreakBrokenInfo: () => void;
}

const DailyBonusContext = createContext<DailyBonusContextType | undefined>(undefined);

// Session storage key for dismiss state (clears on browser close, persists on refresh)
const SESSION_KEY = 'daily_bonus_dismissed';

export const useDailyBonus = () => {
  const context = useContext(DailyBonusContext);
  if (context === undefined) {
    throw new Error('useDailyBonus must be used within a DailyBonusProvider');
  }
  return context;
};

const DATA_VERIFICATION_TOAST =
  'Please complete data verification first and then you will be eligible to claim daily bonus.';

export const DailyBonusProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const { requestShow, notifyDismissed, shouldShow } = useModalQueue();
  const { loading, refetch, canClaim, pendingCount, enabled } = useDailyBonusStatus();
  const navigate = useNavigate();
  const { data: verificationData } = useVerificationStatusQuery();
  const isDataVerified = verificationData?.dataVerificationStatus === 'completed';
  const [streakBrokenInfo, setStreakBrokenInfoState] = useState<StreakBrokenInfo | null>(null);

  // Subscribe to streak broken events from processDailyBonusLogin
  useEffect(() => {
    const unsubscribe = dailyBonusEvents.onStreakBroken((payload) => {
      setStreakBrokenInfoState({
        streakBroken: payload.streakBroken,
        previousStreak: payload.previousStreak,
      });
    });

    return unsubscribe;
  }, []);

  // Clear streak broken info when user logs out
  useEffect(() => {
    if (!user) {
      setStreakBrokenInfoState(null);
      dailyBonusEvents.clearPending();
    }
  }, [user]);

  const setStreakBrokenInfo = useCallback((info: StreakBrokenInfo | null) => {
    setStreakBrokenInfoState(info);
  }, []);

  const clearStreakBrokenInfo = useCallback(() => {
    setStreakBrokenInfoState(null);
  }, []);

  // Dismiss the modal for this session
  const dismissDailyBonusModal = useCallback(() => {
    sessionStorage.setItem(SESSION_KEY, 'true');
    notifyDismissed('DAILY_BONUS');
  }, [notifyDismissed]);

  // Open the modal manually (e.g., from sidebar); only if data verification is complete
  const openDailyBonusModal = useCallback(() => {
    if (!isDataVerified) {
      toast.info(DATA_VERIFICATION_TOAST);
      navigate('/profile');
      return;
    }
    requestShow('DAILY_BONUS');
  }, [isDataVerified, requestShow, navigate]);

  // Clear session storage when user logs out
  useEffect(() => {
    if (!user) {
      sessionStorage.removeItem(SESSION_KEY);
    }
  }, [user]);

  // Get visibility from queue
  const showDailyBonusModal = shouldShow('DAILY_BONUS');

  const value: DailyBonusContextType = {
    showDailyBonusModal,
    setShowDailyBonusModal: () => {}, // Deprecated - use queue system
    openDailyBonusModal,
    dismissDailyBonusModal,
    canClaim,
    pendingCount,
    enabled,
    loading,
    refetch,
    streakBrokenInfo,
    setStreakBrokenInfo,
    clearStreakBrokenInfo,
  };

  return (
    <DailyBonusContext.Provider value={value}>
      {children}
    </DailyBonusContext.Provider>
  );
};
